(() => {
  if (window.BlazeXNewPageInitialized) return;
  window.BlazeXNewPageInitialized = true;

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const mobileMediaTabsQuery = window.matchMedia('(max-width: 989px)');

  const revealSelectedTab = (tab) => {
    const tabList = tab.closest('.bx-feature-tabs__buttons, .bx-steps__tabs, .bx-occasions .bx-tabs');
    if (!tabList) return;

    window.requestAnimationFrame(() => {
      const hasHorizontalOverflow = tabList.scrollWidth > tabList.clientWidth;
      const hasVerticalOverflow = tabList.scrollHeight > tabList.clientHeight;
      const tabRect = tab.getBoundingClientRect();
      const listRect = tabList.getBoundingClientRect();
      const scrollportLeft = listRect.left + tabList.clientLeft;
      const scrollportTop = listRect.top + tabList.clientTop;

      if (hasHorizontalOverflow) {
        const left = tabList.scrollLeft + tabRect.left - scrollportLeft - (tabList.clientWidth - tabRect.width) / 2;
        const maxLeft = tabList.scrollWidth - tabList.clientWidth;
        tabList.scrollTo({ left: Math.max(0, Math.min(left, maxLeft)), behavior: 'smooth' });
        return;
      }

      if (hasVerticalOverflow) {
        const top = tabList.scrollTop + tabRect.top - scrollportTop - (tabList.clientHeight - tabRect.height) / 2;
        const maxTop = tabList.scrollHeight - tabList.clientHeight;
        tabList.scrollTo({ top: Math.max(0, Math.min(top, maxTop)), behavior: 'smooth' });
      }
    });
  };

  const initializeScrollableTabs = (root = document) => {
    root.querySelectorAll('.bx-feature-tabs__buttons, .bx-occasions .bx-tabs').forEach((tabList) => {
      if (tabList.dataset.bxTabScrollReady === 'true') return;
      tabList.dataset.bxTabScrollReady = 'true';
      tabList.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-bx-tab]');
        if (tab && tabList.contains(tab)) revealSelectedTab(tab);
      });
    });
  };

  const syncTabs = (group) => {
    const buttons = [...group.querySelectorAll('[data-bx-tab]')];
    const panels = [...group.querySelectorAll('[data-bx-panel]')];
    if (!buttons.length || !panels.length) return;

    const activeButton = buttons.find((button) => button.classList.contains('is-active')) || buttons[0];
    const activeKey = activeButton.dataset.bxTab;
    const mediaContainer = group.querySelector('[data-bx-media-container]');
    const mediaPanels = [...group.querySelectorAll('.bx-media-tabs__media-panel[data-bx-panel]')];
    const mobileMediaSlots = [...group.querySelectorAll('[data-bx-mobile-media]')];

    buttons.forEach((button) => {
      const selected = button.dataset.bxTab === activeKey;
      button.classList.toggle('is-active', selected);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(selected));
      button.setAttribute('aria-expanded', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });

    buttons.forEach((button) => {
      const mediaPanel = mediaPanels.find((panel) => panel.dataset.bxPanel === button.dataset.bxTab);
      const mobileMediaSlot = mobileMediaSlots.find((slot) => slot.dataset.bxMobileMedia === button.dataset.bxTab);
      const destination = mobileMediaTabsQuery.matches ? mobileMediaSlot : mediaContainer;
      if (mediaPanel && destination && mediaPanel.parentElement !== destination) destination.append(mediaPanel);
    });

    panels.forEach((panel) => {
      const selected = panel.dataset.bxPanel === activeKey;
      panel.classList.toggle('is-active', selected);
      panel.setAttribute('role', 'tabpanel');
      panel.hidden = !selected;
    });
  };

  mobileMediaTabsQuery.addEventListener('change', () => {
    document.querySelectorAll('[data-bx-tabs]').forEach(syncTabs);
  });

  const syncAccordion = (item) => {
    const button = item.querySelector(':scope > [data-bx-accordion]');
    const content = button?.nextElementSibling;
    if (!button || !content) return;
    const open = item.classList.contains('is-open');
    button.setAttribute('aria-expanded', String(open));
    content.hidden = !open;
  };

  const initializeCreatorsCarousel = (carousel) => {
    if (carousel.dataset.bxCreatorsReady === 'true') return;

    const track = carousel.querySelector('[data-bx-creators-track]');
    const previous = carousel.querySelector('[data-bx-creators-prev]');
    const next = carousel.querySelector('[data-bx-creators-next]');
    const dots = carousel.querySelector('[data-bx-creators-dots]');
    const controls = carousel.querySelector('.bx-creators__controls');
    const slides = track ? [...track.children] : [];
    if (!track || !slides.length) return;

    carousel.dataset.bxCreatorsReady = 'true';

    const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;
    const desktopVisibleSlides = () => {
      const firstSlide = slides[0];
      const gap = Number.parseFloat(window.getComputedStyle(track).gap) || 0;
      const slideWidth = firstSlide.getBoundingClientRect().width || 1;
      return Math.max(1, Math.floor((track.clientWidth + gap) / (slideWidth + gap)));
    };
    const lastIndex = () => isDesktop()
      ? Math.max(0, slides.length - desktopVisibleSlides())
      : slides.length - 1;
    let desktopIndex = 0;

    const selectSlide = (index, behavior = 'smooth') => {
      const selectedIndex = Math.max(0, Math.min(index, lastIndex()));
      const slide = slides[selectedIndex];
      if (isDesktop()) {
        desktopIndex = selectedIndex;
        track.style.transform = `translateX(${-slide.offsetLeft}px)`;
        updateControls();
        return;
      }
      track.scrollTo({ left: slide.offsetLeft, behavior });
    };

    const activeIndex = () => {
      if (isDesktop()) return desktopIndex;
      let closestIndex = 0;
      let closestDistance = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
        if (distance < closestDistance) {
          closestIndex = index;
          closestDistance = distance;
        }
      });
      return closestIndex;
    };

    const updateControls = () => {
      const index = activeIndex();
      const canScroll = lastIndex() > 0;
      controls?.classList.toggle('is-static', !canScroll);
      dots?.querySelectorAll('button').forEach((dot, dotIndex) => {
        const selected = dotIndex === index;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-current', String(selected));
      });
      if (previous) previous.disabled = !canScroll || index === 0;
      if (next) next.disabled = !canScroll || index === lastIndex();
    };

    if (dots && slides.length > 1) {
      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bx-creators__dot';
        dot.setAttribute('aria-label', `Go to creator ${index + 1}`);
        dot.addEventListener('click', () => selectSlide(index));
        dots.append(dot);
      });
    }

    previous?.addEventListener('click', () => selectSlide(activeIndex() - 1));
    next?.addEventListener('click', () => selectSlide(activeIndex() + 1));
    track.addEventListener('scroll', () => {
      if (!isDesktop()) updateControls();
    }, { passive: true });
    window.addEventListener('resize', () => {
      if (isDesktop()) {
        track.scrollLeft = 0;
        selectSlide(desktopIndex, 'auto');
      } else {
        track.style.transform = '';
        track.scrollTo({ left: slides[Math.min(desktopIndex, slides.length - 1)].offsetLeft, behavior: 'auto' });
      }
      updateControls();
    });
    updateControls();
  };

  const initializeUgcHoverVideos = (root = document) => {
    const mobileUgcQuery = window.matchMedia('(max-width: 749px)');
    const visuals = [...root.querySelectorAll('[data-bx-ugc-hover-video]')];
    const isCentered = (visual) => {
      const track = visual.closest('[data-bx-creators-track]');
      const card = visual.closest('.bx-ugc-showcase__card');
      if (!track || !card) return true;
      const trackRect = track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const distance = Math.abs((cardRect.left + cardRect.width / 2) - (trackRect.left + trackRect.width / 2));
      return distance <= Math.max(12, cardRect.width * 0.12);
    };

    visuals.forEach((visual) => {
      if (visual.dataset.bxUgcHoverReady === 'true') return;
      visual.dataset.bxUgcHoverReady = 'true';

      const activeVideo = () => [...visual.querySelectorAll('video')].find((video) => video.offsetParent !== null);
      const syncPlayingState = () => {
        const video = activeVideo();
        const isCentering = visual.dataset.bxUgcCentering === 'true';
        if (mobileUgcQuery.matches && !isCentering && video && !video.paused && !video.ended && !isCentered(visual)) {
          stop();
          return;
        }
        visual.classList.toggle('is-playing', Boolean(video && !video.paused && !video.ended));
      };
      const start = (event) => {
        if (event.pointerType === 'touch') return;
        const video = activeVideo();
        if (!video) return;
        video.currentTime = 0;
        video.play().catch(syncPlayingState);
      };
      const stop = () => {
        visual.querySelectorAll('video').forEach((video) => {
          video.pause();
          video.currentTime = 0;
        });
        delete visual.dataset.bxUgcCentering;
        visual.classList.remove('is-playing');
      };
      const stopOtherVideos = () => {
        const track = visual.closest('[data-bx-creators-track]');
        track?.querySelectorAll('[data-bx-ugc-hover-video]').forEach((item) => {
          if (item !== visual) item._bxStopUgcVideo?.();
        });
      };
      const playOnMobile = (allowOffCenter = false) => {
        const video = activeVideo();
        if (!video || (!allowOffCenter && !isCentered(visual))) return;
        video.currentTime = 0;
        video.play().catch(syncPlayingState);
      };

      visual._bxStopUgcVideo = stop;

      visual.querySelectorAll('video').forEach((video) => {
        video.addEventListener('playing', syncPlayingState);
        video.addEventListener('pause', syncPlayingState);
        video.addEventListener('ended', syncPlayingState);
      });

      visual.addEventListener('pointerenter', start);
      visual.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') stop();
      });
      visual.addEventListener('click', (event) => {
        if (!mobileUgcQuery.matches) return;
        event.preventDefault();
        const wasPlaying = visual.classList.contains('is-playing');
        stopOtherVideos();
        if (wasPlaying) {
          stop();
          return;
        }

        const track = visual.closest('[data-bx-creators-track]');
        const card = visual.closest('.bx-ugc-showcase__card');
        if (!track || !card || isCentered(visual)) {
          playOnMobile();
          return;
        }

        track.scrollTo({
          left: card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2,
          behavior: 'smooth'
        });
        visual.dataset.bxUgcCentering = 'true';
        playOnMobile(true);
        window.setTimeout(() => {
          delete visual.dataset.bxUgcCentering;
          if (!isCentered(visual)) stop();
        }, 360);
      });
    });

    [...new Set(visuals.map((visual) => visual.closest('[data-bx-creators-track]')).filter(Boolean))].forEach((track) => {
      if (track.dataset.bxUgcCenterReady === 'true') return;
      track.dataset.bxUgcCenterReady = 'true';
      let frame = 0;
      track.addEventListener('scroll', () => {
        if (!mobileUgcQuery.matches || frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          track.querySelectorAll('[data-bx-ugc-hover-video].is-playing').forEach((visual) => {
            if (visual.dataset.bxUgcCentering !== 'true' && !isCentered(visual)) visual._bxStopUgcVideo?.();
          });
        });
      }, { passive: true });
    });
  };

  const initializeInteractiveSteps = (root = document) => {
    root.querySelectorAll('[data-bx-interactive-steps]').forEach((section) => {
      if (section.dataset.bxStepsReady === 'true') return;
      const tabs = [...section.querySelectorAll('[data-bx-step-tab]')];
      const panels = [...section.querySelectorAll('[data-bx-step-panel]')];
      if (!tabs.length || !panels.length) return;
      section.dataset.bxStepsReady = 'true';

      const pauseVideo = (panel) => panel.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
      const playVideo = (panel) => {
        const video = [...panel.querySelectorAll('video')].find((item) => item.offsetParent !== null);
        if (video) video.play().catch(() => {});
      };
      const activate = (key, play = false) => {
        tabs.forEach((tab) => {
          const selected = tab.dataset.bxStepTab === key;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          const selected = panel.dataset.bxStepPanel === key;
          panel.classList.toggle('is-active', selected);
          if (selected && play) playVideo(panel);
          if (!selected) pauseVideo(panel);
        });
      };

      const current = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
      activate(current.dataset.bxStepTab);
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        activate(tab.dataset.bxStepTab, true);
        revealSelectedTab(tab);
      }));
      panels.forEach((panel) => {
        panel.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch' && window.matchMedia('(min-width: 750px)').matches) playVideo(panel);
        });
        panel.addEventListener('pointerleave', () => pauseVideo(panel));
      });
    });
  };

  const initializeHeroPreviewVideos = (root = document) => {
    root.querySelectorAll('[data-bx-hero-preview-video]').forEach((preview) => {
      if (preview.dataset.bxHeroPreviewReady === 'true') return;
      preview.dataset.bxHeroPreviewReady = 'true';

      const videos = [...preview.querySelectorAll('.bx-color-hero__preview-video video')];
      if (!videos.length) return;
      const autoplay = preview.dataset.bxHeroAutoplay === 'true';

      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const activeVideo = () => videos.find((video) => video.offsetParent !== null);
      const showPoster = () => preview.classList.remove('is-video-playing');
      const pause = () => {
        videos.forEach((video) => video.pause());
        showPoster();
      };
      const play = () => {
        const video = activeVideo();
        videos.forEach((item) => {
          if (item !== video) item.pause();
        });
        showPoster();
        if (!video) return;

        video.muted = true;
        video.defaultMuted = true;
        const start = () => video.play().catch(showPoster);
        if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
          start();
        } else {
          video.addEventListener('canplay', start, { once: true });
        }
      };

      videos.forEach((video) => {
        video.addEventListener('playing', () => {
          if (video === activeVideo()) preview.classList.add('is-video-playing');
        });
        video.addEventListener('waiting', showPoster);
        video.addEventListener('stalled', showPoster);
        video.addEventListener('error', showPoster);
      });

      mobileQuery.addEventListener('change', () => {
        if (autoplay) play();
        else pause();
      });
      if (autoplay) play();
      else pause();
    });
  };

  const initializeVideoPrewarming = (root = document) => {
    if (!('IntersectionObserver' in window)) return;

    const videos = [...root.querySelectorAll('video[preload="metadata"]')]
      .filter((video) => video.offsetParent !== null && video.dataset.bxVideoPrewarmReady !== 'true');
    if (!videos.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) return;

        const video = entry.target;
        video.dataset.bxVideoPrewarmReady = 'true';
        video.preload = 'auto';
        video.load();
        observer.unobserve(video);
      });
    }, { rootMargin: '100% 0px', threshold: 0.01 });

    videos.forEach((video) => observer.observe(video));
  };

  const initializeProductCarouselCartForms = (root = document) => {
    root.querySelectorAll('[data-bx-product-carousel-form]').forEach((form) => {
      if (form.dataset.bxCartReady === 'true') return;
      form.dataset.bxCartReady = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const button = event.submitter || form.querySelector('[type="submit"]');
        if (!button || button.disabled || button.dataset.bxCartSubmitting === 'true') return;

        button.dataset.bxCartSubmitting = 'true';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        try {
          const data = new FormData(form);
          data.append('sections', 'cart_data,mini_cart');
          data.append('sections_url', window.location.pathname);

          const response = await fetch(`${form.action.replace(/\/$/, '')}.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: data
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || 'Unable to add this product to the cart.');

          if (result.sections && window.T4SThemeSP?.Cart?.renderContents) {
            window.T4SThemeSP.isATCSuccess = false;
            window.T4SThemeSP.Cart.renderContents(result.sections);
          }

          const drawer = document.getElementById('t4s-mini_cart');
          const jquery = window.jQuery || window.$;
          if (drawer && jquery && window.T4SThemeSP?.Drawer?.opend) {
            window.T4SThemeSP.Drawer.opend(jquery(drawer));
          }
        } catch (error) {
          if (window.T4SThemeSP?.Notices) window.T4SThemeSP.Notices(error.message);
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          delete button.dataset.bxCartSubmitting;
        }
      });
    });
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-bx-tabs]').forEach(syncTabs);
    initializeScrollableTabs(root);
    root.querySelectorAll('.bx-accordion__item, .bx-spec-group').forEach(syncAccordion);
    root.querySelectorAll('[data-bx-creators-carousel]').forEach(initializeCreatorsCarousel);
    initializeUgcHoverVideos(root);
    initializeInteractiveSteps(root);
    initializeHeroPreviewVideos(root);
    initializeVideoPrewarming(root);
    initializeProductCarouselCartForms(root);
  };

  const closeModal = (modal) => {
    if (!modal) return;
    modal.querySelectorAll('video').forEach((video) => video.pause());
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.removeProperty('overflow');
    modal._bxTrigger?.focus();
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-bx-tab]');
    if (tab) {
      const group = tab.closest('[data-bx-tabs]');
      if (group) {
        group.querySelectorAll('[data-bx-tab]').forEach((button) => button.classList.toggle('is-active', button === tab));
        syncTabs(group);
      }
      return;
    }

    const accordionButton = event.target.closest('[data-bx-accordion]');
    if (accordionButton) {
      const item = accordionButton.closest('.bx-accordion__item, .bx-spec-group');
      if (item) {
        if (item.classList.contains('bx-accordion__item')) {
          const accordion = item.closest('.bx-accordion');
          accordion?.querySelectorAll(':scope > .bx-accordion__item').forEach((accordionItem) => {
            accordionItem.classList.toggle('is-open', accordionItem === item);
            syncAccordion(accordionItem);
          });
        } else {
          item.classList.toggle('is-open');
          syncAccordion(item);
        }
      }
      return;
    }

    const opener = event.target.closest('[data-bx-modal-open]');
    if (opener) {
      const modal = document.getElementById(opener.dataset.bxModalOpen);
      if (modal) {
        modal._bxTrigger = opener;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        modal.querySelector(focusableSelector)?.focus();
        const video = [...modal.querySelectorAll('video')].find((item) => item.offsetParent !== null) || modal.querySelector('video');
        if (video) {
          video.currentTime = 0;
          video.play().catch(() => {});
        }
      }
      return;
    }

    const closer = event.target.closest('[data-bx-modal-close]');
    if (closer) {
      closeModal(closer.closest('.bx-modal'));
      return;
    }

    if (event.target.classList.contains('bx-modal')) closeModal(event.target);
  });

  document.addEventListener('keydown', (event) => {
    const modal = document.querySelector('.bx-modal.is-open');
    if (!modal) return;

    if (event.key === 'Escape') {
      closeModal(modal);
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = [...modal.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  initialize();
})();
